import { styled } from '@mui/material/styles';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import { FORD_COLORS, FORD_TYPOGRAPHY, FORD_SPACING, FORD_RADIUS, FORD_SHADOWS } from '../../theme/fordTheme.js';

// Common constants to reduce duplication
const COMMON_COLORS = {
  primary: '#4f7cff',
  primaryHover: '#3d63cc',
  primaryLight: '#233058',
  border: '#233058',
  hoverBackground: 'rgba(79, 124, 255, 0.1)',
  contextBackground: 'rgba(79, 124, 255, 0.08)',
  alertRed: '#cf3636',
  alertRedBackground: 'rgba(220, 53, 69, 0.1)',
  statusGreen: '#28a745',
  statusOrange: '#fd7e14',
  statusRed: '#dc3545',
  statusGray: '#6c757d',
};

const COMMON_SIZES = {
  fontSize: '0.875rem',
  borderRadius: '8px',
  padding: '8px 12px',
};

// Main container - White theme
export const CommandCenterContainer = styled(Box)(({ theme }) => ({
  height: '100vh',
  maxHeight: '100vh',
  backgroundColor: FORD_COLORS.backGroundColor,
  color: '#333333',
  fontFamily: FORD_TYPOGRAPHY.fallbackFont,
  padding: '28px',
  width: '100vw',
  minWidth: '100vw',
  boxSizing: 'border-box',
  overflowY: 'auto',
  overflowX: 'hidden',
  margin: 0,
  borderRadius: 0,
}));

// Outer container for content
export const CommandCenterOuterContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '100vw',
  margin: '0 auto',
  padding: '0',
  // boxSizing: 'border-box',
  // overflow: 'hidden',
}));

// Page header styling - White theme
export const CommandCenterPageHeader = styled(Box)(({ theme }) => ({
  '& h2': {
    fontSize: FORD_TYPOGRAPHY.h4,
    fontWeight: FORD_TYPOGRAPHY.extraBold,
    fontFamily: FORD_TYPOGRAPHY.newfont,
    color: 'rgb(19, 34, 52)',
    letterSpacing: FORD_TYPOGRAPHY.letterSpacingWide,
  },
  '& .subtitle': {
    color: 'rgb(94, 110, 126)',
    fontSize: FORD_TYPOGRAPHY.body2,
    fontFamily: FORD_TYPOGRAPHY.newfont,
    marginTop: '4px',
  }
}));

// Layout container for main content
export const CommandCenterLayout = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  width: '100%',
  height: 'auto',
}));

// Main content area
export const CommandCenterMainContent = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: FORD_SPACING.lg,
  width: '100%',
  minHeight: 0, // Allow content to shrink
}));

// Critical Alert Banner
export const CommandCenterCriticalAlert = styled(Box)(({ theme }) => ({
  backgroundColor: COMMON_COLORS.alertRed,
  borderRadius: FORD_RADIUS.md,
  padding: FORD_SPACING.lg,
  marginBottom: FORD_SPACING.xs,
  boxShadow: FORD_SHADOWS.md,
}));

export const CommandCenterAlertContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  color: '#ffffff',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: FORD_SPACING.md,
    alignItems: 'flex-start',
  },
}));

export const CommandCenterAlertIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginRight: FORD_SPACING.md,
  '& .MuiSvgIcon-root': {
    fontSize: '2rem',
    color: '#ffffff',
  },
  [theme.breakpoints.down('md')]: {
    marginRight: 0,
  },
}));

export const CommandCenterAlertText = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: FORD_TYPOGRAPHY.semibold,
  color: '#ffffff',
  flex: 1,
  [theme.breakpoints.down('md')]: {
    fontSize: '1.1rem',
  },
}));

export const CommandCenterAlertStats = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: FORD_SPACING.xl,
  [theme.breakpoints.down('md')]: {
    gap: FORD_SPACING.lg,
    width: '100%',
    justifyContent: 'space-around',
  },
}));

export const CommandCenterAlertStat = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  color: '#ffffff',
  minWidth: '60px',
}));

// Sites Section
export const CommandCenterSitesSection = styled(Box)(({ theme }) => ({
  marginTop: FORD_SPACING.xs,
}));

export const CommandCenterSitesTitle = styled(Box)(({ theme }) => ({
  marginBottom: '10px',
  '& h3': {
    fontSize: '15px',
    fontWeight: '700',
    color: 'rgb(19, 34, 52);',
    margin: '0px 0px 14px',
    fontFamily: FORD_TYPOGRAPHY.newfont
  },
}));

export const CommandCenterSitesGrid = styled(Box)(({ theme }) => ({
  width: '100%',
}));

// Site Card Components
export const CommandCenterSiteCard = styled(Box)(({ theme, statusColor }) => {
  // Use the statusColor directly if it's a valid hex color, otherwise fallback to gray
//   const borderColor = statusColor?.startsWith('#') ? statusColor : COMMON_COLORS.statusGray;
   const borderColor = (statusColor?.startsWith('#') || statusColor?.startsWith('hsl(')) ? statusColor : COMMON_COLORS.statusGray;

  return {
    backgroundColor: '#ffffff',
    border: `1px solid ${borderColor}`,
    borderTop: `3px solid ${borderColor}`,
    borderRadius: '8px',
    padding: '6px',
    height: '120px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    transition: 'all 0.2s ease-in-out',
    minWidth: '180px',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
    },
  };
});

export const CommandCenterSiteHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: FORD_SPACING.sm,
}));

export const CommandCenterSiteName = styled(Typography)(({ theme }) => ({
  fontSize: '17px',
  fontWeight: '800',
  color: 'rgb(19, 34, 52)',
  margin: 0,
  fontFamily: FORD_TYPOGRAPHY.newfont
}));

export const CommandCenterSiteSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: 'rgb(94, 110, 126)',
  margin: 0,
  lineHeight: 1.1,
  marginTop: '1px'
}));

// Convert hex color to rgba with reduced alpha for background
const hexToRgba = (hex, alpha) => {
  const r = Number.parseInt(hex.slice(1, 3), 16);
  const g = Number.parseInt(hex.slice(3, 5), 16);
  const b = Number.parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const CommandCenterSiteStatus = styled(Box)(({ theme, statusColor }) => {
  // Use the statusColor directly if it's a valid hex color, otherwise fallback to gray

  const fullColor = (statusColor?.startsWith('#') || statusColor?.startsWith('hsl(')) ? statusColor : COMMON_COLORS.statusGray;

  const backgroundColorWithAlpha = fullColor?.startsWith('#') ? hexToRgba(fullColor, 0.15) : 
    fullColor?.startsWith('hsl(') ? fullColor.replace('hsl(', 'hsla(').replace(')', ', 0.15)') : 
    'rgba(108, 117, 125, 0.15)';


  return {
    backgroundColor: backgroundColorWithAlpha,
    color: fullColor,
    padding: '4px 8px',
    borderRadius: '99px',
    fontSize: '11px',
    fontWeight: FORD_TYPOGRAPHY.extraBold,
    letterSpacing: '0.5px',
    marginLeft: '4px',
    fontFamily: FORD_TYPOGRAPHY.newfont
  };
});

export const CommandCenterSiteStats = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
}));

export const CommandCenterSiteOnline = styled(Typography)(({ theme }) => ({
 fontFamily: FORD_TYPOGRAPHY.newfont, 
 fontSize: '12px',
  color: 'rgb(94, 110, 126)',
  margin: 0
}));

// Loading and Error Components
export const CommandCenterLoadingMessage = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: FORD_SPACING.xl,
  color: FORD_COLORS.textMuted,
  fontSize: FORD_TYPOGRAPHY.body1,
  fontFamily: FORD_TYPOGRAPHY.fallbackFont,
}));

export const CommandCenterErrorMessage = styled(Typography)(({ theme }) => ({
  color: '#ef4444',
  fontSize: FORD_TYPOGRAPHY.body1,
  fontFamily: FORD_TYPOGRAPHY.fallbackFont,
  textAlign: 'center',
}));

export const CommandCenterPrimaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: COMMON_COLORS.primary,
  color: FORD_COLORS.textLight,
  padding: `${FORD_SPACING.sm} ${FORD_SPACING.lg}`,
  borderRadius: FORD_RADIUS.sm,
  fontSize: COMMON_SIZES.fontSize,
  fontWeight: FORD_TYPOGRAPHY.medium,
  fontFamily: FORD_TYPOGRAPHY.fallbackFont,
  textTransform: 'none',
  border: 'none',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: COMMON_COLORS.primaryHover,
    transform: 'translateY(-1px)',
    boxShadow: FORD_SHADOWS.md,
  },
  '&:active': {
    transform: 'translateY(0)',
  },
}));

export const CommandCenterSpinner = styled(CircularProgress)(({ theme }) => ({
  color: COMMON_COLORS.primary,
  width: '20px !important',
  height: '20px !important',
}));
